import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, Frown, ArrowRight, RotateCcw, Share2, HelpCircle, Check, X } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { useLang } from "@/lib/i18n";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { CandleChart } from "@/components/CandleChart";
import { toast } from "@/components/ui/sonner";
import { sfx } from "@/lib/sounds";

function fmt2(n) {
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const CONFETTI_COLORS = ["#EAB308", "#22C55E", "#EF4444", "#ffffff", "#67E8F9"];

function Confetti() {
  const pieces = useMemo(() => Array.from({ length: 90 }, (_, i) => ({
    id: `p${i}`, left: Math.random() * 100, delay: Math.random() * 0.9, dur: 2 + Math.random() * 2,
  })), []);
  return (
    <div className="confetti-layer" data-testid="confetti">
      {pieces.map((p, i) => (
        <span
          key={p.id}
          className="confetti-piece"
          style={{
            left: `${p.left}%`,
            background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.dur}s`,
          }}
        />
      ))}
    </div>
  );
}

export default function Results() {
  const { id } = useParams();
  const { t } = useLang();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [state, setState] = useState(null);
  const [replay, setReplay] = useState(null);
  const [active, setActive] = useState(null);
  const [guess, setGuess] = useState(null);
  const [confetti, setConfetti] = useState(false);
  const navigatedRef = useRef(false);
  const wonPlayedRef = useRef(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/matches/${id}`);
      setState(data);
      refreshUser();
      if (data.me?.won && !wonPlayedRef.current) {
        wonPlayedRef.current = true;
        sfx.win();
        setConfetti(true);
        setTimeout(() => setConfetti(false), 6000);
      }
      try {
        const g = await api.get(`/matches/${id}/guess`);
        setGuess(g.data);
      } catch (e) { console.warn("guess options unavailable:", e); }
    })();
  }, [id, refreshUser]);

  // fetch replay only after the player has guessed (keeps the name hidden)
  useEffect(() => {
    if (!guess?.guessed || replay) return;
    (async () => {
      try {
        const rep = await api.get(`/matches/${id}/replay`);
        setReplay(rep.data);
        setActive(rep.data.instruments[0]?.id);
      } catch (e) { console.warn("replay unavailable:", e); }
    })();
  }, [guess?.guessed, id, replay]);

  // multiplayer: follow the host's rematch
  useEffect(() => {
    if (!state?.is_multiplayer) return;
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get(`/matches/${id}`);
        if (data.rematch_id && !navigatedRef.current) {
          navigatedRef.current = true;
          navigate(`/match/${data.rematch_id}`);
        }
      } catch (e) { console.warn("rematch poll failed:", e); }
    }, 2000);
    return () => clearInterval(iv);
  }, [state?.is_multiplayer, id, navigate]);

  const submitGuess = async (symbol) => {
    try {
      const { data } = await api.post(`/matches/${id}/guess`, { symbol });
      setGuess({ guessed: true, correct: data.correct, bonus: data.bonus, actual: data.actual, actual_name: data.actual_name });
      if (data.correct) { sfx.win(); setConfetti(true); setTimeout(() => setConfetti(false), 5000); refreshUser(); }
      else sfx.lose();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const doRematch = async () => {
    try {
      const { data } = await api.post(`/matches/${id}/rematch`);
      navigatedRef.current = true;
      navigate(`/match/${data.match_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const shareResult = async () => {
    const c = document.createElement("canvas");
    c.width = 1200; c.height = 630;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#0A0A0A"; ctx.fillRect(0, 0, 1200, 630);
    ctx.fillStyle = "#EAB308"; ctx.fillRect(0, 0, 1200, 8);
    ctx.fillStyle = "#EAB308"; ctx.font = "900 44px Chivo, sans-serif";
    ctx.fillText("SNETCH", 60, 100);
    ctx.fillStyle = "#737373"; ctx.font = "400 22px 'JetBrains Mono', monospace";
    ctx.fillText("the real trading match online", 60, 140);
    const won = state?.me?.won;
    ctx.fillStyle = "#ffffff"; ctx.font = "900 64px Chivo, sans-serif";
    ctx.fillText(won ? "VITTORIA" : `#${state?.me?.rank}`, 60, 280);
    const profit = state?.me?.profit ?? 0;
    ctx.fillStyle = profit >= 0 ? "#22C55E" : "#EF4444";
    ctx.font = "700 120px 'JetBrains Mono', monospace";
    ctx.fillText(`${profit >= 0 ? "+" : ""}${fmt2(profit)}`, 60, 430);
    ctx.fillStyle = "#A3A3A3"; ctx.font = "400 24px 'JetBrains Mono', monospace";
    ctx.fillText("skill over luck · 10 min · 100.000 budget", 60, 540);
    await new Promise((res) => {
      c.toBlob(async (blob) => {
        const file = new File([blob], "snetch-result.png", { type: "image/png" });
        try {
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: "Snetch", text: `Snetch: ${profit >= 0 ? "+" : ""}${fmt2(profit)}` });
          } else {
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = "snetch-result.png"; a.click();
            URL.revokeObjectURL(url);
            toast.success(t("share_result"));
          }
        } catch (e) {
          if (e?.name !== "AbortError") { console.warn("share failed:", e); toast.error(t("share_failed")); }
        }
        res();
      }, "image/png");
    });
  };

  const startNew = async () => {
    const { data } = await api.post("/matches");
    navigate(`/match/${data.id}`);
  };

  if (!state) {
    return <div className="min-h-screen bg-base flex items-center justify-center text-white/40 font-mono animate-pulse">…</div>;
  }

  const me = state.me || { won: false, rank: "-", profit: 0 };
  const profit = me.profit ?? 0;
  const won = me.won;

  const inst = replay?.instruments.find((i) => i.id === active) || replay?.instruments[0];
  let markers = [], tooltips = {};
  if (inst && replay) {
    const tradeMarkers = replay.trades.filter((tr) => tr.instrument_id === inst.id).map((tr) => ({
      step: tr.step, position: tr.side === "buy" ? "belowBar" : "aboveBar",
      color: tr.side === "buy" ? "#22C55E" : "#EF4444", shape: tr.side === "buy" ? "arrowUp" : "arrowDown",
      text: tr.side === "buy" ? "B" : "S",
    }));
    const eventMarkers = (inst.events || []).map((e) => ({
      step: e.step, position: "aboveBar", color: "#EAB308", shape: "circle", text: "•".repeat(e.importance),
    }));
    markers = [...eventMarkers, ...tradeMarkers];
    tooltips = Object.fromEntries((inst.events || []).map((e) =>
      [e.step, `${t("event_news")} · ${t("importance")}: ${"•".repeat(e.importance)}`]));
  }

  return (
    <div className="min-h-screen bg-base text-white">
      {confetti && <Confetti />}
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-14">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
          className="border border-white/10 bg-panel p-10 text-center relative overflow-hidden"
        >
          <div className={`absolute inset-0 ${won ? "bg-[radial-gradient(circle_at_50%_0%,rgba(34,197,94,0.15),transparent_60%)]" : "bg-[radial-gradient(circle_at_50%_0%,rgba(239,68,68,0.1),transparent_60%)]"}`} />
          <div className="relative">
            {won ? <Trophy className="w-14 h-14 text-signal mx-auto" /> : <Frown className="w-14 h-14 text-white/40 mx-auto" />}
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-white/40 mt-6">{t("results_title")}</div>
            <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tight mt-2" data-testid="results-outcome">
              {won ? t("won") : `#${me.rank} · ${t("lost")}`}
            </h1>
            <div className="mt-6">
              <div className="font-mono text-xs uppercase tracking-widest text-white/40">{t("your_profit")}</div>
              <div className={`font-mono font-black text-5xl tabular-nums mt-1 ${profit >= 0 ? "text-profit" : "text-loss"}`} data-testid="results-profit">
                {profit >= 0 ? "+" : ""}{fmt2(profit)}
              </div>
            </div>
          </div>
        </motion.div>

        {state.pot > 0 && (
          <div data-testid="pot-banner" className={`mt-6 border p-5 flex items-center justify-between ${state.pot_winner === user?.id ? "border-profit/50 bg-profit/5" : "border-white/10 bg-panel"}`}>
            <div className="font-mono text-sm text-white/60 uppercase tracking-widest">{t("pot")}</div>
            <div className="flex items-center gap-3">
              {state.pot_winner === user?.id && (
                <span className="font-mono text-xs uppercase tracking-widest text-profit">{t("pot_won")}</span>
              )}
              <span className={`font-mono font-black text-2xl tabular-nums ${state.pot_winner === user?.id ? "text-profit" : "text-signal"}`}>
                €{state.pot.toFixed(2)}
              </span>
            </div>
          </div>
        )}

        {/* GUESS THE INSTRUMENT */}
        {guess && !guess.guessed && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="mt-8 border border-signal/40 bg-signal/5 p-6" data-testid="guess-panel">
            <div className="flex items-center gap-2 text-signal">
              <HelpCircle className="w-5 h-5" />
              <span className="font-heading font-bold text-lg">{t("guess_title")}</span>
            </div>
            <p className="mt-1 text-sm text-white/60">{t("guess_sub")}</p>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {guess.options.map((o) => (
                <button
                  key={o.symbol}
                  data-testid={`guess-option-${o.symbol}`}
                  onClick={() => submitGuess(o.symbol)}
                  className="text-left px-4 py-3 border border-white/15 bg-panel hover:bg-panelhover hover:border-signal/50 rounded-sm transition-colors"
                >
                  <div className="font-mono font-bold text-sm">{o.symbol}</div>
                  <div className="font-mono text-xs text-white/50">{o.name}</div>
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {guess?.guessed && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            data-testid="guess-result"
            className={`mt-8 border p-6 flex items-center justify-between ${guess.correct ? "border-profit/50 bg-profit/5" : "border-loss/40 bg-loss/5"}`}>
            <div className="flex items-center gap-3">
              {guess.correct ? <Check className="w-6 h-6 text-profit" /> : <X className="w-6 h-6 text-loss" />}
              <div>
                <div className={`font-heading font-bold text-lg ${guess.correct ? "text-profit" : "text-loss"}`}>
                  {guess.correct ? t("guess_correct") : t("guess_wrong")}
                </div>
                <div className="font-mono text-sm text-white/60">{t("was_word")}: <span className="text-white">{guess.actual} · {guess.actual_name}</span></div>
              </div>
            </div>
            {guess.correct && guess.bonus > 0 && (
              <div className="font-mono font-bold text-profit tabular-nums">+{guess.bonus} {t("bonus_word")}</div>
            )}
          </motion.div>
        )}

        {/* final leaderboard */}
        <div className="mt-8 border border-white/10">
          {state.leaderboard.map((e) => (
            <div key={e.name} data-testid={`result-rank-${e.rank}`}
              className={`flex items-center justify-between px-5 py-4 border-b border-white/10 last:border-b-0 ${e.is_you ? "bg-signal/5" : "bg-panel"}`}>
              <div className="flex items-center gap-4">
                <span className={`font-mono font-bold text-lg tabular-nums w-6 ${e.rank === 1 ? "text-signal" : "text-white/40"}`}>{e.rank}</span>
                <span className={`font-mono ${e.is_you ? "text-signal font-bold" : "text-white/80"}`}>
                  {e.is_you ? t("you") : e.name}{e.is_bot ? " ·bot" : ""}
                </span>
              </div>
              <div className="flex items-center gap-8">
                <span className="font-mono text-sm text-white/50 tabular-nums">{fmt2(e.equity)}</span>
                <span className={`font-mono font-bold tabular-nums ${e.profit >= 0 ? "text-profit" : "text-loss"}`}>
                  {e.profit >= 0 ? "+" : ""}{fmt2(e.profit)}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* REPLAY (revealed only after guessing) */}
        {replay && inst && (
          <div className="mt-10">
            <h2 className="font-heading font-bold text-xl mb-4 flex items-center gap-2">{t("replay")}</h2>
            <div className="border border-white/10 bg-panel">
              <div className="flex items-stretch border-b border-white/10 overflow-x-auto">
                {replay.instruments.map((i) => (
                  <button key={i.id} data-testid={`replay-tab-${i.symbol}`} onClick={() => setActive(i.id)}
                    className={`px-4 py-3 text-left border-r border-white/10 transition-colors min-w-[120px] ${i.id === active ? "bg-panelhover" : "hover:bg-panelhover/50"}`}>
                    <div className="font-mono font-bold text-sm">{i.symbol}</div>
                    <div className="font-mono text-xs text-white/40">{i.is_real ? t("real_data") : t("sim_data")}</div>
                  </button>
                ))}
              </div>
              <div className="p-2">
                <CandleChart candles={inst.candles} height={340} markers={markers} tooltips={tooltips} resetKey={inst.id} />
              </div>
              <div className="border-t border-white/10 px-4 py-2 font-mono text-[11px] text-white/40 flex gap-4 flex-wrap">
                <span><span className="text-profit">▲ B</span> = {t("buy")}</span>
                <span><span className="text-loss">▼ S</span> = {t("sell")}</span>
                <span><span className="text-signal">•</span> = {t("event_news")}</span>
              </div>
            </div>

            <div className="mt-4 border border-white/10 bg-panel p-4">
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2">{t("your_trades")}</div>
              {replay.trades.length === 0 ? (
                <div className="text-white/30 font-mono text-sm py-2">{t("no_trades")}</div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 font-mono text-xs">
                  {replay.trades.map((tr, i) => (
                    <div key={`${tr.step}-${tr.instrument_id}-${tr.action}-${i}`} data-testid={`replay-trade-${i}`} className="flex items-center justify-between border border-white/10 px-3 py-2">
                      <span className="text-white/40">#{tr.step}</span>
                      <span className={tr.side === "buy" ? "text-profit" : "text-loss"}>{tr.action.toUpperCase()} {tr.symbol}</span>
                      <span className="tabular-nums text-white/70">{fmt2(tr.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          {state.is_multiplayer && state.is_host && (
            <Button data-testid="rematch-btn" onClick={doRematch} className="bg-white/5 border border-white/20 hover:bg-white/10 text-white rounded-sm font-semibold h-12 px-8">
              <RotateCcw className="w-5 h-5" /> {t("rematch")}
            </Button>
          )}
          {state.is_multiplayer && !state.is_host && (
            <div className="flex items-center gap-2 text-white/50 font-mono text-sm px-4">{t("rematch_wait")}</div>
          )}
          <Button data-testid="share-btn" onClick={shareResult} className="bg-white/5 border border-white/20 hover:bg-white/10 text-white rounded-sm font-semibold h-12 px-8">
            <Share2 className="w-5 h-5" /> {t("share")}
          </Button>
          <Button data-testid="play-again-btn" onClick={startNew} className="bg-signal text-black hover:bg-white rounded-sm font-semibold h-12 px-8">
            {t("play_again")} <ArrowRight className="w-5 h-5" />
          </Button>
          <Button data-testid="back-lobby-btn" onClick={() => navigate("/lobby")} variant="outline" className="border-white/20 hover:bg-white/5 rounded-sm h-12 px-8">
            {t("back_lobby")}
          </Button>
        </div>
      </div>
    </div>
  );
}
